import { store } from '../store';
import { vehicleUpdated, vehicleDisconnected } from '../store/vehiclesSlice';
import { alertReceived, alertRemoved } from '../store/alertsSlice';
import { toast } from 'react-toastify';

const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:8000';
let ws = null;
let reconnectTimer = null;
let videoFrameCallbacks = new Set();

export function onVideoFrame(cb) {
  if (cb) {
    videoFrameCallbacks.add(cb);
  } else {
    // Clear all — legacy compat
    videoFrameCallbacks.clear();
  }
  return () => videoFrameCallbacks.delete(cb);
}

const HANDLERS = {
  'connection.established': (data) => {
    console.log('[WS] Central connecté, role:', data.role);
  },
  'vehicle.update': (data) => {
    store.dispatch(vehicleUpdated(data));
  },
  'vehicle.disconnected': (data) => {
    store.dispatch(vehicleDisconnected(data.vehicle_id));
  },
  'alert.new': (data) => {
    store.dispatch(alertReceived(data));
    toast.warning(`🚨 Nouvelle alerte: ${data.kind} — Plaque: ${data.plate}`, { autoClose: 8000 });
  },
  'sos.received': (data) => {
    store.dispatch(vehicleUpdated({ ...data, status: 'sos' }));
    toast.error(`🆘 SOS de ${data.plate} (${data.driver_name}) — ${data.reason}`, {
      autoClose: false,
    });
  },
  'sos.cancelled': (data) => {
    toast.info(`SOS annulé par véhicule ${data.vehicle_id}`);
    store.dispatch(vehicleUpdated({ vehicle_id: data.vehicle_id, status: 'active' }));
  },
  'sos.status_changed': (data) => {
    const labels = { responding: 'En cours de traitement', resolved: 'Résolu' };
    if (data.status === 'resolved') {
      toast.success(`✅ SOS #${data.sos_id} résolu${data.vehicle_plate ? ` — ${data.vehicle_plate}` : ''}`);
      store.dispatch(vehicleUpdated({ vehicle_id: data.vehicle_id, status: 'active' }));
    } else {
      toast.info(`🆘 SOS #${data.sos_id} → ${labels[data.status] || data.status}`);
    }
    // Dispatch a custom event so SOSManagement can reload
    window.dispatchEvent(new CustomEvent('sos-update', { detail: data }));
  },
  'alert.cancelled': (data) => {
    store.dispatch(alertRemoved(data.alert_id));
    toast.info(`Alerte #${data.alert_id} annulée`);
  },
  'alert.status_changed': (data) => {
    const statusLabels = { verified: 'Vérifiée', in_progress: 'En cours', resolved: 'Résolue', rejected: 'Rejetée', cancelled: 'Annulée' };
    toast.info(`Alerte #${data.alert_id} → ${statusLabels[data.status] || data.status}`);
  },
  'violation.new': (data) => {
    toast.warn(`⚡ Excès: ${data.plate} — ${data.speed}/${data.limit} km/h`);
  },
  // Phase 2
  'geofence.violation': (data) => {
    toast.error(`🚫 ${data.plate} est entré en zone ${data.kind}: ${data.geofence}`, { autoClose: false });
  },
  'fatigue.alert': (data) => {
    toast.warn(`😴 ${data.plate} roule depuis ${data.hours}h sans pause`);
  },
  'driver.checkin': (data) => {
    toast.success(`✅ ${data.driver_name} — Check-in (${data.plate})`);
  },
  'driver.checkout': (data) => {
    toast.info(`🔚 ${data.driver_name} — Check-out (${data.plate})`);
  },
  // SOS Video streaming
  'sos.video_started': (data) => {
    toast.error(`📹 Vidéo SOS en direct de ${data.plate}`, { autoClose: false, toastId: `video-${data.vehicle_id}` });
    videoFrameCallbacks.forEach((cb) => cb({ type: 'started', vehicle_id: data.vehicle_id, plate: data.plate }));
  },
  'sos.video_frame': (data) => {
    videoFrameCallbacks.forEach((cb) => cb({ type: 'frame', ...data }));
  },
  'sos.video_stopped': (data) => {
    toast.info(`📹 Vidéo SOS terminée (véhicule ${data.vehicle_id})`);
    videoFrameCallbacks.forEach((cb) => cb({ type: 'stopped', vehicle_id: data.vehicle_id }));
  },
  'sos.video_clip_ready': (data) => {
    toast.info(`🎬 Nouveau clip vidéo de ${data.plate} (#${data.clip_number})`);
    videoFrameCallbacks.forEach((cb) => cb({ type: 'clip_ready', ...data }));
  },
  'sos.video_clip_uploading': (data) => {
    videoFrameCallbacks.forEach((cb) => cb({ type: 'clip_uploading', ...data }));
  },
  // INTELLIGENT: Crash detection alerts
  'crash.detected': (data) => {
    const sevColors = { minor: 'info', moderate: 'warn', severe: 'error', critical: 'error' };
    const method = sevColors[data.severity] || 'error';
    toast[method](
      `💥 CRASH ${data.severity.toUpperCase()} — ${data.plate} (${data.driver_name || ''}) | ${data.decel_g}G | ${data.speed_before}→${data.speed_after} km/h`,
      { autoClose: false, toastId: `crash-${data.vehicle_id}-${Date.now()}` }
    );
    if (data.auto_sos) {
      store.dispatch(vehicleUpdated({ vehicle_id: data.vehicle_id, status: 'sos' }));
    }
  },
  'crash.warning': (data) => {
    toast.warn(`⚠️ Freinage violent: ${data.plate} (${data.decel_g}G) — ${data.severity}`);
  },
};

export function connectCentralWS() {
  const token = localStorage.getItem('token');
  if (!token) return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;

  clearTimeout(reconnectTimer);
  ws = new WebSocket(`${WS_URL}/ws/vehicle/?token=${token}`);

  ws.onopen = () => {
    console.log('[WS] Connecté à la centrale');
  };

  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data);
      const handler = HANDLERS[msg.type];
      if (handler) handler(msg.data);
    } catch (e) {
      console.warn('[WS] Parse error', e);
    }
  };

  ws.onclose = () => {
    console.log('[WS] Déconnecté, reconnexion...');
    ws = null;
    reconnectTimer = setTimeout(connectCentralWS, 3000);
  };

  ws.onerror = () => { try { if (ws && ws.readyState !== WebSocket.CLOSED) ws.close(); } catch (e) { /* ignore */ } };
}

export function sendWS(type, data = {}) {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type, data }));
  }
}

export function disconnectCentralWS() {
  clearTimeout(reconnectTimer);
  if (ws) { ws.close(); ws = null; }
}
